/**
 * Desktop / tablet left sidebar.
 *
 * Pixel-faithful port of `WebSidebar` in `design/project/screens-web-shell.jsx`.
 * Tablet (`collapsed`) → 72px icon-only rail; wide → 244px labeled rail.
 *
 * Nav items match the spec's tab list (home / all-tx / analytics / cats /
 * recur / search / notif). The seven-row order mirrors the design source.
 */
import Icon, { type IconName } from '@/components/common/Icon';

export type SidebarTabKey =
  | 'home'
  | 'all-tx'
  | 'analytics'
  | 'cats'
  | 'recur'
  | 'search'
  | 'notif';

export interface SidebarProps {
  active: SidebarTabKey;
  onTab: (k: SidebarTabKey) => void;
  onAdd?: () => void;
  onSettings?: () => void;
  onBookClick?: () => void;
  bookName?: string;
  bookColor?: string;
  collapsed?: boolean;
  userName?: string;
  userEmail?: string;
  userAvatarChar?: string;
}

/**
 * First displayable character of `s`. Uses the iterator protocol so
 * surrogate-pair emoji / CJK characters count as one glyph rather than
 * yielding half a codepoint. Empty / whitespace inputs fall back to '·'
 * (a visible dot) so the chip never renders a blank square.
 */
function firstChar(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return '·';
  return [...trimmed][0] ?? '·';
}

const ITEMS: Array<{ k: SidebarTabKey; label: string; icon: IconName }> = [
  { k: 'home', label: '首頁', icon: 'home' },
  { k: 'all-tx', label: '全部紀錄', icon: 'calendar' },
  { k: 'analytics', label: '分析', icon: 'chart' },
  { k: 'cats', label: '分類', icon: 'tag' },
  { k: 'recur', label: '固定週期', icon: 'repeat' },
  { k: 'search', label: '搜尋', icon: 'search' },
  { k: 'notif', label: '通知', icon: 'bell' },
];

export default function Sidebar({
  active,
  onTab,
  onAdd,
  onSettings,
  onBookClick,
  bookName = '個人',
  bookColor = 'var(--terracotta-500)',
  collapsed = false,
  userName = '陳安安',
  userEmail = 'andrew@mochia.app',
  userAvatarChar = '安',
}: SidebarProps): JSX.Element {
  return (
    <aside
      style={{
        width: collapsed ? 72 : 244,
        flexShrink: 0,
        background: 'var(--cream-100)',
        borderRight: '1px solid var(--border-1)',
        display: 'flex',
        flexDirection: 'column',
        padding: collapsed ? '20px 10px' : '20px 14px',
        gap: 4,
        transition: 'width var(--dur-base) var(--ease-out)',
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
      aria-label="主導覽"
    >
      {/* Brand */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '4px 6px 14px',
          minHeight: 42,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: 'var(--mocha-600)',
            color: 'var(--cream-50)',
            display: 'grid',
            placeItems: 'center',
            font: '700 16px var(--font-cjk-display)',
          }}
        >
          摸
        </div>
        {!collapsed && (
          <div
            style={{
              font: '700 17px var(--font-cjk-display)',
              color: 'var(--fg-1)',
              letterSpacing: '-0.01em',
            }}
          >
            Mochia
          </div>
        )}
      </div>

      {/* Book switcher.
        *
        * Bug B fix: when the sidebar is collapsed (tablet rail), the design
        * source only shows a coloured dot — but with no other text, users
        * can't tell which book is active. We instead render a compact
        * book chip with the first character of the book name (mirroring
        * the avatar pattern used at the bottom of the sidebar). Falls back
        * to the dot when the name is empty. */}
      <button
        type="button"
        onClick={onBookClick}
        title={collapsed ? bookName : undefined}
        aria-label={collapsed ? `帳本：${bookName}` : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-1)',
          borderRadius: 12,
          padding: collapsed ? '8px' : '10px 12px',
          cursor: 'pointer',
          boxShadow: 'var(--shadow-1)',
          marginBottom: 8,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        {collapsed ? (
          <span
            data-testid="sidebar-book-chip"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: bookColor,
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              font: '600 13px var(--font-cjk-display)',
              flexShrink: 0,
            }}
          >
            {firstChar(bookName)}
          </span>
        ) : (
          <>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                background: bookColor,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                font: '500 13px var(--font-cjk)',
                color: 'var(--fg-1)',
                flex: 1,
                textAlign: 'left',
              }}
            >
              {bookName}
            </span>
            <Icon name="chevD" size={14} stroke="var(--fg-3)" />
          </>
        )}
      </button>

      {/* Add button (desktop FAB equivalent) */}
      <button
        type="button"
        onClick={onAdd}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          justifyContent: collapsed ? 'center' : 'flex-start',
          background: 'var(--accent)',
          color: '#fff',
          border: 0,
          borderRadius: 12,
          padding: collapsed ? '10px' : '11px 14px',
          cursor: 'pointer',
          font: '600 14px var(--font-cjk)',
          boxShadow: '0 4px 10px rgba(184,118,84,0.28)',
          marginBottom: 14,
        }}
      >
        <Icon name="plus" size={16} stroke="#fff" />
        {!collapsed && <span>新增紀錄</span>}
      </button>

      {/* Nav */}
      <nav
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          flex: 1,
        }}
      >
        {ITEMS.map((it) => {
          const isActive = active === it.k;
          return (
            <button
              key={it.k}
              type="button"
              onClick={() => onTab(it.k)}
              title={collapsed ? it.label : undefined}
              aria-current={isActive ? 'page' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: collapsed ? '10px' : '10px 12px',
                borderRadius: 10,
                background: isActive ? 'var(--bg-surface)' : 'transparent',
                border: 0,
                cursor: 'pointer',
                textAlign: 'left',
                color: isActive ? 'var(--fg-1)' : 'var(--fg-2)',
                font: isActive
                  ? '600 14px var(--font-cjk)'
                  : '500 14px var(--font-cjk)',
                boxShadow: isActive ? 'var(--shadow-1)' : 'none',
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <Icon
                name={it.icon}
                size={18}
                stroke={isActive ? 'var(--mocha-700)' : 'var(--fg-2)'}
              />
              {!collapsed && <span>{it.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Settings */}
      <button
        type="button"
        onClick={onSettings}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '10px' : '10px 12px',
          background: 'transparent',
          border: 0,
          borderRadius: 10,
          cursor: 'pointer',
          color: 'var(--fg-2)',
          font: '500 14px var(--font-cjk)',
        }}
      >
        <Icon name="settings" size={18} stroke="var(--fg-2)" />
        {!collapsed && <span>設定</span>}
      </button>

      {!collapsed && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 6px 0',
            borderTop: '1px solid var(--border-1)',
            marginTop: 6,
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              background: 'var(--terracotta-500)',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              font: '600 12px var(--font-cjk-display)',
            }}
          >
            {userAvatarChar}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: '500 13px var(--font-cjk)',
                color: 'var(--fg-1)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userName}
            </div>
            <div
              style={{
                font: '400 11px var(--font-cjk)',
                color: 'var(--fg-3)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userEmail}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
